import { useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import { linearScale } from '@aytm/app/stats/page/question-views/components/lib/linear-scale'

const MAX_PARTICLE_AGE = 50
const POINTS_AMOUNT = 400
const FRAME_RATE = 25
// The particle field is a flourish on first draw, not a live animation: it stops once a particle's
// whole life has played (50 frames at 25 fps). Master meant the same stop and never reached it -- its
// timer cleared a handle the draw loop did not write -- so the field ran for as long as the page did.
const RUNTIME_MS = (MAX_PARTICLE_AGE * 1000) / FRAME_RATE
const LINE_WIDTH = 2
const ARROW_COLOR = 'rgba(255, 255, 255, 0.4)'
const TRAIL_FILL = 'rgba(0, 0, 0, 0.94)'

const getRandomPoint = (W, H) => {
  const x = Math.random() * (W - 1)
  const y = Math.random() * (H - 1)
  return { x, y, xt: x, yt: y, age: Math.ceil(Math.random() * MAX_PARTICLE_AGE) }
}

const Windmap = ({
  chartId,
  windmap,
  width,
  height,
  margin,
  videoDuration,
  visible = true,
  stopAfterMs = RUNTIME_MS
}) => {
  const canvasRef = useRef(null)
  const rafRef = useRef(null)
  const timerRef = useRef(null)
  const stopTimerRef = useRef(null)

  useEffect(() => {
    if (!visible) return
    if (!windmap || !Object.keys(windmap).length) return
    if (!canvasRef.current) return

    const canvas = canvasRef.current
    const g = canvas.getContext('2d', { willReadFrequently: true })

    const { nx, ny, dx: dxData, dy: dyData } = windmap
    const W = width
    const H = height
    const KOEFF_Y = Math.max(Math.round(videoDuration / 30), 0.5)
    // Guard a zero duration (no <video> + no payload duration) so the field stays degenerate instead of Infinity
    const MULTIPLIER_X = videoDuration ? (7 * nx) / videoDuration : 0
    const MULTIPLIER_Y = videoDuration ? (KOEFF_Y * nx) / videoDuration : 0

    // fillStyle stays set across frames — renderParticles uses it as the alpha
    // mask for the destination-in trail fade. Do not reassign inside the loop.
    g.fillStyle = TRAIL_FILL
    g.lineWidth = LINE_WIDTH
    g.strokeStyle = ARROW_COLOR

    const particles = []
    const bucket = []
    for (let i = 0; i <= POINTS_AMOUNT; i++) particles.push(getRandomPoint(W, H))

    const scaleX = linearScale([0, W - 1], [0, nx])
    const scaleY = linearScale([0, H - 1], [0, ny])

    const field = (x, y) => {
      const sx = Math.floor(scaleX(x))
      const sy = Math.floor(scaleY(y))
      const pos = sy * nx + sx
      if (dxData[pos] && dyData[pos]) {
        const dx = dxData[pos] * MULTIPLIER_X
        const dy = dyData[pos] * MULTIPLIER_Y
        return [dx, dy, Math.sqrt(dx * dx + dy * dy)]
      }
      return []
    }

    const updateParticles = () => {
      bucket.length = 0
      for (let i = 0; i < particles.length; i++) {
        const particle = particles[i]
        const np = { ...particle }

        if (particle.age > MAX_PARTICLE_AGE) {
          const p = getRandomPoint(W, H)
          np.x = p.x
          np.y = p.y
          np.age = 0
        } else {
          np.x = particle.xt
          np.y = particle.yt
        }

        const f = field(np.x, np.y)
        if (f[0] && f[1]) {
          const xt = np.x + f[0]
          const yt = np.y + f[1]
          const vt = field(xt, yt)
          np.xt = xt
          np.yt = yt
          np.m = f[2]

          // Read `particle.age` (pre-reset value), not `np.age` — a just-reset
          // particle must still enter the bucket on the same tick.
          if (vt[0] && vt[1] && particle.age > 0) {
            if (np.m < 0.8) np.age = MAX_PARTICLE_AGE
            bucket.push(np)
          } else {
            np.age = MAX_PARTICLE_AGE
          }
        }
        np.age += 1
        particles[i] = np
      }
    }

    const renderParticles = () => {
      // Trail fade: destination-in + fillRect attenuates alpha of prior frames
      // before stroking the new bucket on top.
      const prev = g.globalCompositeOperation
      g.globalCompositeOperation = 'destination-in'
      g.fillRect(0, 0, W, H)
      g.globalCompositeOperation = prev

      for (const p of bucket) {
        g.beginPath()
        g.moveTo(p.x, p.y)
        g.lineTo(p.xt, p.yt)
        g.stroke()
      }
    }

    const draw = () => {
      timerRef.current = setTimeout(() => {
        rafRef.current = requestAnimationFrame(draw)
      }, 1000 / FRAME_RATE)

      updateParticles()
      renderParticles()
    }

    draw()

    if (stopAfterMs != null) {
      stopTimerRef.current = setTimeout(() => {
        if (timerRef.current) {
          clearTimeout(timerRef.current)
          timerRef.current = null
        }
        if (rafRef.current) {
          cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
      }, stopAfterMs)
    }

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current)
      if (rafRef.current) cancelAnimationFrame(rafRef.current)
      if (stopTimerRef.current) clearTimeout(stopTimerRef.current)
      g.clearRect(0, 0, W, H)
    }
  }, [windmap, width, height, videoDuration, visible, stopAfterMs])

  if (!visible) return null
  if (!windmap || !Object.keys(windmap).length) return null

  return (
    <canvas
      ref={canvasRef}
      id={`map-${chartId}`}
      width={width}
      height={height}
      style={{
        transform: 'scale(1, -1)',
        position: 'absolute',
        top: `${margin[0]}px`,
        left: `${margin[1]}px`,
        pointerEvents: 'none'
      }}
    />
  )
}

Windmap.propTypes = {
  chartId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  windmap: PropTypes.shape({
    nx: PropTypes.number,
    ny: PropTypes.number,
    dx: PropTypes.array,
    dy: PropTypes.array
  }),
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  margin: PropTypes.arrayOf(PropTypes.number).isRequired,
  videoDuration: PropTypes.number.isRequired,
  visible: PropTypes.bool,
  stopAfterMs: PropTypes.number
}

Windmap.displayName = 'Windmap'

export default Windmap
